'use client';

import { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import { cn } from '@/lib/utils';
import { CharFadeLabel } from '@/components/effects/CharFadeLabel';
import { useLocale } from '@/lib/i18n/LocaleContext';
import { locales, switchLocalePath, type Locale } from '@/lib/i18n/config';

function useNavLinks() {
  const { dict } = useLocale();
  return [
    { href: '/', label: dict.header.nav.home },
    { href: '/portfolio', label: dict.header.nav.portfolio },
    { href: '/products', label: dict.header.nav.products },
    { href: '/about-datacode', label: dict.header.nav.about },
    { href: '/contact', label: dict.header.nav.contact },
    { href: '/careers', label: dict.header.nav.careers },
  ];
}

/* ── Nav link with underline-slide hover (matching dilacode style) and a
      soft per-character fade ripple on load / hover ── */
function NavLink({ href, label, isDarkHeader, isActive, decodeDelay }: {
  href: string;
  label: string;
  isDarkHeader: boolean;
  isActive: boolean;
  decodeDelay: number;
}) {
  const { dir } = useLocale();
  const rtl = dir === 'rtl';
  return (
    <Link
      href={href}
      className={cn(
        'group relative text-sm font-medium transition-opacity duration-300',
        isDarkHeader ? 'text-white' : 'text-black',
        isActive ? 'opacity-100' : 'opacity-60 hover:opacity-100'
      )}
      style={
        rtl
          ? { fontFamily: 'var(--font-rtl)', fontSize: '16px', fontWeight: 600 }
          : { fontFamily: 'var(--font-poppins), Poppins, sans-serif', fontSize: '14px', fontWeight: 500 }
      }
    >
      <CharFadeLabel text={label} mountDelay={decodeDelay} />
      {/* Underline slide-in from left on hover */}
      <span
        className="absolute -bottom-[5px] left-0 h-px w-full origin-left scale-x-0 transition-transform duration-300 ease-out group-hover:scale-x-100"
        style={{ background: isDarkHeader ? '#ffffff' : '#000000' }}
        aria-hidden="true"
      />
    </Link>
  );
}

const LOCALE_LABEL_KEY: Record<Locale, 'en' | 'ar' | 'ckb'> = {
  en: 'en',
  ar: 'ar',
  ckb: 'ckb',
};

/* ── Compact language switcher: EN / عربي / کوردی. Shares the header's
      nav-link visual language (opacity states, small mono-ish sizing). ── */
function LanguageSwitcher({ isDarkHeader, className }: { isDarkHeader: boolean; className?: string }) {
  const { locale, dict } = useLocale();
  const pathname = usePathname();

  return (
    <nav aria-label={dict.header.languageSwitcher.ariaLabel} className={cn('flex items-center gap-3', className)}>
      {locales.map((target) => {
        const isCurrent = target === locale;
        return (
          <Link
            key={target}
            href={switchLocalePath(pathname, target)}
            hrefLang={target}
            lang={target}
            aria-current={isCurrent ? 'true' : undefined}
            className={cn(
              'rtl-label text-xs font-medium uppercase tracking-wide transition-opacity duration-300',
              target === 'ar' && 'font-cairo',
              target === 'ckb' && 'font-sorani',
              isDarkHeader ? 'text-white' : 'text-black',
              isCurrent ? 'opacity-100' : 'opacity-50 hover:opacity-90'
            )}
          >
            {dict.header.languageSwitcher[LOCALE_LABEL_KEY[target]]}
          </Link>
        );
      })}
    </nav>
  );
}

export function Header() {
  const pathname = usePathname();
  const { dict, href } = useLocale();
  const navLinks = useNavLinks();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isDarkHeader, setIsDarkHeader] = useState(true);
  const headerRef = useRef<HTMLElement>(null);

  // Handle scroll for header background
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Freeze the iOS safe-area top inset. In-browser Safari live-updates
  // env(safe-area-inset-top): correct at first paint, but when the browser
  // chrome minimizes on scroll the reported inset collapses while the page
  // stays edge-to-edge under the status bar — so an env()-padded header
  // slides up under the clock. Measure the inset with a probe element and
  // keep the largest value seen for the current orientation in
  // --dc-safe-top (see globals.css for the pre-hydration fallback).
  // Desktop and Android measure 0px, so nothing changes there.
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const probe = document.createElement('div');
    probe.setAttribute('data-safe-area-probe', '');
    probe.style.cssText =
      'position:fixed;top:0;left:0;width:0;visibility:hidden;pointer-events:none;height:env(safe-area-inset-top, 0px)';
    document.body.appendChild(probe);

    let frozen = 0;
    let lastPortrait = window.innerHeight >= window.innerWidth;

    const measure = () => {
      const portrait = window.innerHeight >= window.innerWidth;
      const inset = probe.getBoundingClientRect().height;
      // Same orientation: only ever grow (ignores the scroll-collapsed 0).
      // Orientation flip: re-baseline (landscape top inset is genuinely 0).
      frozen = portrait === lastPortrait ? Math.max(frozen, inset) : inset;
      lastPortrait = portrait;
      document.documentElement.style.setProperty('--dc-safe-top', `${frozen}px`);
    };

    measure();
    window.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('resize', measure);
      probe.remove();
      document.documentElement.style.removeProperty('--dc-safe-top');
    };
  }, []);

  // All pages start with dark header (white text/logo on dark bg).
  useEffect(() => {
    setIsDarkHeader(true);
  }, [pathname]);

  // Logo swap based on scroll (light-section triggers)
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const lightSections = document.querySelectorAll('.light-section');

    const checkInitial = () => {
      document.querySelectorAll('.light-section').forEach((section) => {
        const rect = section.getBoundingClientRect();
        if (rect.top <= 80 && rect.bottom >= 80) {
          setIsDarkHeader(false);
        }
      });
    };
    checkInitial();
    const timer = setTimeout(checkInitial, 100);

    // Track our own triggers — killing ScrollTrigger.getAll() would tear down
    // triggers owned by page components (portfolio pinning, skills fades).
    // GSAP is dynamic-imported so the Header (in every page's layout) doesn't
    // put it in every entry chunk (TASK-4).
    let cancelled = false;
    const triggers: Array<{ kill: () => void }> = [];
    if (lightSections.length > 0) {
      (async () => {
        const { ScrollTrigger } = await import('@/lib/gsap');
        if (cancelled) return;
        lightSections.forEach((section) => {
          triggers.push(
            ScrollTrigger.create({
              trigger: section,
              start: 'top 80px',
              end: 'bottom 80px',
              onEnter: () => setIsDarkHeader(false),
              onLeave: () => setIsDarkHeader(true),
              onEnterBack: () => setIsDarkHeader(false),
              onLeaveBack: () => setIsDarkHeader(true),
            }),
          );
        });
      })();
    }

    return () => {
      cancelled = true;
      clearTimeout(timer);
      triggers.forEach((t) => t.kill());
    };
  }, [pathname]);

  // Close menu on route change.
  useEffect(() => {
    setIsMenuOpen(false);
  }, [pathname]);

  // Prevent body scroll when menu is open
  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? 'hidden' : '';
  }, [isMenuOpen]);

  return (
    <>
      {/* ── Header — always transparent, no backdrop on scroll ── */}
      <header
        ref={headerRef}
        className="fixed left-0 right-0 top-0 z-[100] flex items-center justify-between transition-opacity duration-200 delay-200 ease-in-out"
        style={{
          paddingLeft: 'clamp(1.5rem, 8vw, 120px)',
          paddingRight: 'clamp(1.5rem, 8vw, 120px)',
          // Safe-area top: with viewport-fit=cover the header would otherwise
          // sit under the iPhone status bar / notch. --dc-safe-top is the
          // JS-frozen inset (see effect above) — raw env() collapses to 0
          // when Safari's chrome minimizes on scroll.
          paddingTop: 'var(--dc-safe-top, env(safe-area-inset-top, 0px))',
          height: 'calc(clamp(5rem, 8vw, 120px) + var(--dc-safe-top, env(safe-area-inset-top, 0px)))',
        }}
      >
        {/* Glass-blur frozen backdrop — fades in on scroll */}
        <div
          className={cn(
            'pointer-events-none absolute left-0 right-0 top-0 -z-10 transition-opacity duration-500',
            isScrolled && !isMenuOpen ? 'opacity-100' : 'opacity-0'
          )}
          style={{
            // Grow by the safe-area inset so the blur also covers the
            // status-bar region the header now extends under.
            height: 'calc(120px + var(--dc-safe-top, env(safe-area-inset-top, 0px)))',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            maskImage: 'linear-gradient(to bottom, black 50%, transparent 100%)',
            WebkitMaskImage: 'linear-gradient(to bottom, black 50%, transparent 100%)',
            background: isDarkHeader
              ? 'linear-gradient(to bottom, rgba(23,56,53,0.85) 0%, rgba(23,56,53,0.4) 60%, rgba(23,56,53,0) 100%)'
              : 'linear-gradient(to bottom, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.6) 60%, rgba(255,255,255,0) 100%)',
          }}
        />
        {/* Logo */}
        <Link href={href('/')} className="relative z-[101]">
          <div className="relative h-10 w-32">
            {/* White logo (dark header or menu open) */}
            <Image
              src="/images/logo-white.avif"
              alt="Datacode"
              fill
              className={cn(
                'object-contain object-left transition-opacity duration-300',
                (isDarkHeader || isMenuOpen) ? 'opacity-100' : 'opacity-0'
              )}
              priority
              unoptimized
            />
            {/* Black logo (light header) */}
            <Image
              src="/images/logo-black.avif"
              alt="Datacode"
              fill
              className={cn(
                'object-contain object-left transition-opacity duration-300',
                (isDarkHeader || isMenuOpen) ? 'opacity-0' : 'opacity-100'
              )}
              priority
              unoptimized
            />
          </div>
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex md:items-center md:gap-8">
          <ul className="flex items-center gap-10">
            {navLinks.map((link, index) => {
              const localizedLinkHref = href(link.href);
              return (
                <li key={link.href}>
                  <NavLink
                    href={localizedLinkHref}
                    label={link.label}
                    isDarkHeader={isDarkHeader || isMenuOpen}
                    isActive={pathname === localizedLinkHref}
                    decodeDelay={350 + index * 130}
                  />
                </li>
              );
            })}
          </ul>
          <LanguageSwitcher isDarkHeader={isDarkHeader || isMenuOpen} />
        </nav>

        {/* Mobile hamburger */}
        <button
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          className="relative z-[101] flex flex-col gap-1.5 p-2 md:hidden"
          aria-label={dict.header.toggleMenuAriaLabel}
        >
          <span className={cn('h-0.5 w-6 transition-all duration-300', isMenuOpen ? 'bg-white translate-y-2 rotate-45' : isDarkHeader ? 'bg-white' : 'bg-black')} />
          <span className={cn('h-0.5 w-6 transition-all duration-300', isMenuOpen ? 'bg-white opacity-0' : isDarkHeader ? 'bg-white' : 'bg-black')} />
          <span className={cn('h-0.5 w-6 transition-all duration-300', isMenuOpen ? 'bg-white -translate-y-2 -rotate-45' : isDarkHeader ? 'bg-white' : 'bg-black')} />
        </button>
      </header>

      {/* Mobile Menu Overlay */}
      {isMenuOpen && (
        <div
          className="fixed inset-0 z-[98] flex flex-col md:hidden"
          style={{ height: '100dvh', backgroundColor: '#0a0a0a' }}
        >
          {/* Nav links — left-aligned, editorial style */}
          <div
            className="flex flex-1 flex-col justify-center"
            style={{
              paddingLeft: '2rem',
              paddingRight: '2rem',
              // Clear the header AND the status bar (safe-area top inset).
              paddingTop: 'calc(5rem + var(--dc-safe-top, env(safe-area-inset-top, 0px)))',
            }}
          >
            <nav className="flex flex-col" style={{ gap: 0 }}>
              {navLinks.map((link, index) => {
                const localizedLinkHref = href(link.href);
                const isActive = pathname === localizedLinkHref;
                return (
                  <Link
                    key={link.href}
                    href={localizedLinkHref}
                    onClick={() => setIsMenuOpen(false)}
                    className="group block"
                    style={{
                      borderTop: index === 0 ? '1px solid rgba(255,255,255,0.08)' : 'none',
                      borderBottom: '1px solid rgba(255,255,255,0.08)',
                      paddingTop: '1.25rem',
                      paddingBottom: '1.25rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '1rem',
                      textDecoration: 'none',
                    }}
                  >
                    {/* Number index */}
                    <span
                      style={{
                        fontSize: '11px',
                        letterSpacing: '0.15em',
                        color: isActive ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.25)',
                        fontFamily: 'var(--font-poppins), Poppins, sans-serif',
                        fontWeight: 400,
                        minWidth: '2rem',
                        transition: 'color 0.3s',
                      }}
                    >
                      {String(index + 1).padStart(2, '0')}
                    </span>

                    {/* Active indicator line */}
                    <span
                      style={{
                        width: isActive ? '2rem' : '0',
                        height: '1px',
                        backgroundColor: '#ffffff',
                        transition: 'width 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
                        flexShrink: 0,
                      }}
                    />

                    {/* Link label */}
                    <span
                      className="rtl-display-m"
                      style={{
                        fontFamily: 'var(--font-six-caps)',
                        fontSize: 'clamp(3rem, 12vw, 4.5rem)',
                        lineHeight: '0.95',
                        letterSpacing: '0.03em',
                        textTransform: 'uppercase',
                        color: isActive ? '#ffffff' : 'rgba(255,255,255,0.4)',
                        transition: 'color 0.3s, transform 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
                      }}
                    >
                      {/* Ripples on every menu open (overlay remounts) */}
                      <CharFadeLabel text={link.label} mountDelay={120 + index * 90} />
                    </span>
                  </Link>
                );
              })}
            </nav>
            <div style={{ marginTop: '1.5rem' }}>
              <LanguageSwitcher isDarkHeader={true} />
            </div>
          </div>

          {/* Bottom — Contact info + Socials */}
          <div
            style={{
              paddingLeft: '2rem',
              paddingRight: '2rem',
              paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom))',
            }}
          >
            {/* Email */}
            <div style={{ marginBottom: '1.5rem' }}>
              <a
                href="mailto:info@datacode.app"
                style={{
                  fontSize: '13px',
                  letterSpacing: '0.1em',
                  color: 'rgba(255,255,255,0.5)',
                  textDecoration: 'none',
                  fontFamily: 'var(--font-poppins), Poppins, sans-serif',
                }}
              >
                <bdi dir="ltr">{dict.header.email}</bdi>
              </a>
            </div>

            {/* Socials row + tagline */}
            <div className="flex items-center justify-between">
              <div className="flex items-center" style={{ gap: '1.25rem' }}>
                <a href="#" aria-label={dict.header.socials.instagramAriaLabel} style={{ color: 'rgba(255,255,255,0.3)', transition: 'color 0.3s' }}>
                  <svg style={{ width: '18px', height: '18px' }} fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                  </svg>
                </a>
                <a href="#" aria-label={dict.header.socials.linkedinAriaLabel} style={{ color: 'rgba(255,255,255,0.3)', transition: 'color 0.3s' }}>
                  <svg style={{ width: '18px', height: '18px' }} fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                  </svg>
                </a>
                <a href="#" aria-label={dict.header.socials.twitterAriaLabel} style={{ color: 'rgba(255,255,255,0.3)', transition: 'color 0.3s' }}>
                  <svg style={{ width: '18px', height: '18px' }} fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                  </svg>
                </a>
              </div>

              <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.2)', letterSpacing: '0.15em', textTransform: 'uppercase' }}>
                {dict.header.tagline}
              </span>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
